'use client';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

export interface VoicePageControl {
  label: string;
  kind: 'select' | 'date_range' | 'text' | 'action';
  value: string;
  displayValue?: string;
  options?: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
  disabled?: boolean;
  apply: (value: string) => string | Promise<string>;
}
const controls = new WeakMap<Element, () => VoicePageControl>();

/** Shared UI controls expose their real state and callbacks, without polling or copied state. */
export function useVoicePageControl(control: VoicePageControl) {
  const id = useId();
  const latest = useRef(control);
  latest.current = control;
  return useCallback(
    (element: HTMLElement | null) => {
      if (!element) return;
      element.dataset.voiceControl = id;
      controls.set(element, () => latest.current);
    },
    [id]
  );
}

export function readVoicePageControl(element: Element) {
  return controls.get(element)?.();
}

export function voiceSelectOption(options: VoicePageControl['options'], requested: string) {
  const available = (options || []).filter(option => !option.disabled);
  const exact = available.find(option => option.value === requested);
  if (exact) return exact.value;
  const matches = available.filter(
    option => option.label.toLocaleLowerCase() === requested.toLocaleLowerCase()
  );
  if (matches.length !== 1)
    throw new Error('Choose an available filter option by its exact value or label.');
  return matches[0]!.value;
}

/** Only explicitly registered search/draft fields can be read or edited by voice. */
export function useVoiceTextControl(options: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  maxLength?: number;
}) {
  return useVoicePageControl({
    ...options,
    kind: 'text',
    apply(value) {
      if (value.length > (options.maxLength ?? 1000))
        throw new Error('The field value is too long.');
      options.onChange(value);
      return value;
    },
  });
}

const TOGGLE_OPTIONS = [
  { value: 'true', label: 'Show' },
  { value: 'false', label: 'Hide' },
];
export function useVoiceToggleControl(options: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return useVoicePageControl({
    label: options.label,
    kind: 'select',
    value: String(options.checked),
    options: TOGGLE_OPTIONS,
    apply(value) {
      const next = voiceSelectOption(TOGGLE_OPTIONS, value);
      options.onChange(next === 'true');
      return next;
    },
  });
}

/** Actions resolve only after their existing handler succeeds. */
export function useVoicePageAction(options: {
  label: string;
  disabled?: boolean;
  options?: VoicePageControl['options'];
  run: (value: string) => void | Promise<void>;
}) {
  const [receipt, setReceipt] = useState(0);
  const pending = useRef<Array<() => void>>([]);
  const mounted = useRef(true);
  useEffect(() => {
    for (const resolve of pending.current.splice(0)) resolve();
  }, [receipt]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // A successful handler may intentionally replace its own page (for example after a save).
      for (const resolve of pending.current.splice(0)) resolve();
    };
  }, []);
  const choices = options.options || [{ value: 'run', label: options.label }];
  return useVoicePageControl({
    label: options.label,
    disabled: options.disabled,
    kind: 'action',
    value: '',
    options: choices,
    async apply(value) {
      const selected = voiceSelectOption(choices, value);
      await options.run(selected);
      if (!mounted.current) return selected;
      // Publish context only after React commits the handler's state changes.
      await new Promise<void>(resolve => {
        pending.current.push(resolve);
        setReceipt(value => value + 1);
      });
      return selected;
    },
  });
}
